import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Navigation, Users, Car, Headphones, MapPin, Clock, Shield, ArrowRight, ChevronRight, Play, Sparkles } from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const { login, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/demo/status`)
      .then((res) => setDemoEnabled(res.data?.enabled === true))
      .catch(() => setDemoEnabled(false));
    if (new URLSearchParams(window.location.search).get("suspended")) {
      // Wait for the toaster to mount on a fresh page load
      setTimeout(() => toast.error("This account is suspended. Please contact There You Are Solutions.", { id: "account-suspended" }), 300);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      if (user.must_change_password) {
        toast.info("Please choose your own password to continue");
        navigate("/change-password");
        return;
      }
      toast.success("Welcome back!");
      const routes = { client: "/client", driver: "/driver", employer: "/employer" };
      navigate(routes[user.role] || "/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async (role) => {
    setDemoLoading(role);
    try {
      const response = await axios.post(`${API_URL}/api/demo/login/${role}`);
      const { token, user } = response.data;
      
      // Store token and user
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
      
      toast.success(`Welcome to the ${role} demo!`);
      
      const routes = { client: "/client", driver: "/driver", dispatch: "/employer" };
      navigate(routes[role] || "/");
    } catch (error) {
      toast.error("Failed to load demo. Please try again.");
      console.error(error);
    } finally {
      setDemoLoading(null);
    }
  };

  const roles = [
    {
      id: "dispatch",
      title: "Dispatch",
      subtitle: "Fleet Management",
      description: "Manage your fleet, optimize routes, and track all rides in real-time",
      image: "https://images.unsplash.com/photo-1765648684555-de2d0f6af467?w=800&q=80",
      color: "from-violet-500 to-purple-600",
      bgColor: "bg-violet-500",
      lightColor: "bg-violet-100",
      textColor: "text-violet-600",
      icon: Headphones,
      features: ["Live Fleet Map", "Trip Logs & Manifests", "HIPAA Secure Data"],
      noTint: true
    },
    {
      id: "client",
      title: "Client",
      subtitle: "Book Your Ride",
      description: "Schedule rides easily and track your driver's arrival in real-time",
      image: "https://images.unsplash.com/photo-1758686254593-7c4cd55b2621?w=800&q=80",
      color: "from-emerald-500 to-teal-600",
      bgColor: "bg-emerald-500",
      lightColor: "bg-emerald-100",
      textColor: "text-emerald-600",
      icon: Users,
      features: ["Easy Scheduling", "Real-time Tracking", "Accessibility Options"],
      noTint: true
    },
    {
      id: "driver",
      title: "Driver",
      subtitle: "Hit the Road",
      description: "View your schedule, navigate to pickups, and complete rides seamlessly",
      image: "https://static.prod-images.emergentagent.com/jobs/5b1de827-3f18-4164-bcd3-2ea2a97414b6/images/ddba61bb5e0eb90959f08b4dab0ed9bc2fdc577025be24dcec84a93aa58befb7.png",
      color: "from-amber-500 to-orange-600",
      bgColor: "bg-amber-500",
      lightColor: "bg-amber-100",
      textColor: "text-amber-600",
      icon: Car,
      features: ["Assignments & Calendar", "Turn-by-Turn Nav", "Daily Manifest"],
      noTint: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-0 -left-40 w-96 h-96 bg-violet-500/30 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-500/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute -bottom-20 left-1/3 w-96 h-96 bg-amber-500/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30 animate-float">
                <Navigation className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                  <span className="bg-gradient-to-r from-cyan-300 via-white to-purple-300 bg-clip-text text-transparent drop-shadow-lg">
                    There You Are
                  </span>
                </h1>
                <p className="text-sm text-cyan-400 font-semibold tracking-wide">Medical Transportation</p>
              </div>
            </div>
            <Button
              onClick={() => setShowLogin(true)}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6"
              data-testid="header-login-btn"
            >
              Sign In
            </Button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="px-6 pt-12 pb-8 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6 border border-white/10 animate-fade-in">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-sm text-slate-300">Trusted by 500+ healthcare providers</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight animate-fade-in-up">
              <span className="bg-gradient-to-r from-cyan-400 via-violet-400 to-amber-400 bg-clip-text text-transparent">
                Ride Tracking
              </span>
              <br />
              <span className="text-white">Made Simple</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Real-time medical transportation management for dispatchers, clients, and drivers. All in one platform.
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Button
                onClick={() => setShowLogin(true)}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg shadow-cyan-500/30"
                data-testid="hero-get-started-btn"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              {demoEnabled && (
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 h-14 text-lg"
                  onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-watch-demos-btn"
                >
                  Watch Demos Below
                </Button>
              )}
              <Button
                onClick={() => navigate('/sales')}
                size="lg"
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg shadow-green-500/30"
                data-testid="hero-pricing-btn"
              >
                Pricing
              </Button>
            </div>
            
            {/* 14-Day Free Trial Badge */}
            <div className="flex justify-center mt-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <Button
                onClick={() => navigate('/free-trial')}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-full px-8 py-4 h-auto text-lg font-bold shadow-lg shadow-amber-500/30"
                data-testid="start-trial-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Start 14-Day Free Trial
                <span className="ml-2 text-amber-200 text-sm">• No credit card</span>
              </Button>
            </div>
          </div>
        </section>

        {/* Role Cards Section */}
        <section id="demo-section" className="px-6 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white mb-4">Choose Your Portal</h3>
              <p className="text-slate-400">Select your role to access the right tools</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {roles.map((role, index) => (
                <div
                  key={role.id}
                  className="group relative bg-slate-900/50 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-500 hover:scale-[1.02] animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={role.image}
                      alt={role.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Only show bottom gradient for text readability, no color tint */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
                    
                    {/* Icon Badge */}
                    <div className={`absolute top-4 right-4 w-12 h-12 ${role.bgColor} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <role.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="mb-4">
                      <p className={`text-sm font-semibold ${role.textColor} mb-1`}>{role.subtitle}</p>
                      <h4 className="text-2xl font-bold text-white">{role.title}</h4>
                    </div>
                    <p className="text-slate-400 text-sm mb-6">{role.description}</p>
                    
                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {role.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <div className={`w-1.5 h-1.5 rounded-full ${role.bgColor}`} />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="space-y-3">
                      {demoEnabled && (
                        <Button
                          onClick={() => handleDemoLogin(role.id)}
                          disabled={demoLoading === role.id}
                          className={`w-full bg-gradient-to-r ${role.color} text-white rounded-xl h-12 font-semibold group-hover:shadow-lg transition-all`}
                          data-testid={`${role.id}-demo-card-btn`}
                        >
                          {demoLoading === role.id ? (
                            <>Loading Demo...</>
                          ) : (
                            <>
                              <Play className="mr-2 w-4 h-4" />
                              Try {role.title} Demo
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => setShowLogin(true)}
                        variant="outline"
                        className="w-full border-white/20 text-slate-300 hover:bg-white/10 rounded-xl h-10"
                        data-testid={`${role.id}-login-btn`}
                      >
                        Login as {role.title}
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Strip */}
        <section className="px-6 py-12 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: MapPin, label: "Live Tracking", value: "Real-time GPS" },
                { icon: Clock, label: "On-Time Rate", value: "99.9%" },
                { icon: Shield, label: "HIPAA", value: "Compliant" },
                { icon: Users, label: "Active Users", value: "10,000+" },
              ].map((stat, i) => (
                <div key={i} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/5 rounded-xl mb-3">
                    <stat.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8 border-t border-white/5">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">© 2025 There You Are. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/register" className="text-slate-400 hover:text-white text-sm transition-colors">Create Account</Link>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</a>
            </div>
          </div>
        </footer>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowLogin(false)}
          />
          <div className="relative bg-slate-900 rounded-3xl p-8 w-full max-w-md border border-white/10 shadow-2xl animate-scale-in">
            <button
              onClick={() => setShowLogin(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/30">
                <Navigation className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">Welcome Back</h3>
              <p className="text-slate-400 mt-1">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Link 
                    to="/forgot-password"
                    className="text-sm text-cyan-400 hover:text-cyan-300"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-12 bg-slate-800 border-slate-700 focus:border-cyan-500 rounded-xl text-white placeholder:text-slate-500"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <p className="mt-6 text-center text-slate-400">
              Don't have a company account?{" "}
              <Link to="/register" className="text-cyan-400 font-semibold hover:text-cyan-300" data-testid="register-link">
                Register your company
              </Link>
            </p>

            {/* Quick Demo Access (preview environment only) */}
            {demoEnabled && (
            <div className="mt-6 pt-6 border-t border-slate-700" data-testid="quick-demo-section">
              <p className="text-sm text-slate-400 text-center mb-4">Or try a quick demo:</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin("dispatch")}
                  disabled={demoLoading}
                  data-testid="dispatch-demo-btn"
                  className="border-violet-500/50 text-violet-400 hover:bg-violet-500/20 hover:text-violet-300"
                >
                  {demoLoading === "dispatch" ? "..." : "Dispatch"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin("driver")}
                  disabled={demoLoading}
                  data-testid="driver-demo-btn"
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 hover:text-cyan-300"
                >
                  {demoLoading === "driver" ? "..." : "Driver"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin("client")}
                  disabled={demoLoading}
                  data-testid="client-demo-btn"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
                >
                  {demoLoading === "client" ? "..." : "Client"}
                </Button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        
        .animate-fade-in { animation: fade-in 0.6s ease-out forwards; }
        .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; opacity: 0; }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
